import { type PerspectiveCamera, Ray, Vector2 } from '@seddi/three';
import type { Dispatch } from 'redux';

import { setHoverGraphicId, setSelectedGraphicId } from '../../store/appSlice';
import type { Raycaster } from '../../viewer/viewerRaycaster';

const DRAG_THRESHOLD = 0.005;

export class PickControls {
  readonly camera: PerspectiveCamera;
  readonly canvas: HTMLElement;
  readonly raycaster: Raycaster;

  enabled = true;

  hover: string | undefined;
  selection: string | undefined;

  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;

  constructor(
    camera: PerspectiveCamera,
    canvas: HTMLElement,
    raycaster: Raycaster,
    dispatch: Dispatch,
  ) {
    this.camera = camera;
    this.canvas = canvas;
    this.raycaster = raycaster;
    this.dispatch = dispatch;

    const scope = this;

    this.onPointerDown = function (event: PointerEvent) {
      if (!scope.enabled) return;

      if (event.button === 0) {
        scope.pointer.x = (event.offsetX / scope.canvas.clientWidth) * 2 - 1;
        scope.pointer.y = -(event.offsetY / scope.canvas.clientHeight) * 2 + 1;
        scope.ray.origin.setFromMatrixPosition(scope.camera.matrixWorld);
        scope.ray.direction
          .set(scope.pointer.x, scope.pointer.y, 0.5)
          .unproject(scope.camera)
          .sub(scope.ray.origin)
          .normalize();
        scope.raycaster.intersectLogos(scope.ray);

        scope.isDown = true;
        scope.pointerDown.copy(scope.pointer);
        canvas.setPointerCapture(event.pointerId);
        canvas.addEventListener('pointerup', onPointerUp);
      }
    };

    this.onPointerMove = function (event: PointerEvent) {
      if (!scope.enabled) return;

      const { camera, canvas, pointer, ray, raycaster } = scope;

      pointer.x = (event.offsetX / canvas.clientWidth) * 2 - 1;
      pointer.y = -(event.offsetY / canvas.clientHeight) * 2 + 1;

      if (scope.isDown && !scope.isDrag) {
        scope.isDrag = scope.pointerDown.distanceTo(scope.pointer) > DRAG_THRESHOLD;
      }

      ray.origin.setFromMatrixPosition(camera.matrixWorld);
      ray.direction.set(pointer.x, pointer.y, 0.5).unproject(camera).sub(ray.origin).normalize();

      const hit = raycaster.intersectLogos(ray);

      const nextHover = hit.logoId ?? null;
      if (nextHover !== (scope.hover ?? null)) {
        dispatch(setHoverGraphicId(nextHover));
      }
    };

    function onPointerUp(event: PointerEvent) {
      if (!scope.isDrag) {
        if (scope.hover !== scope.selection) {
          dispatch(setSelectedGraphicId(scope.hover ?? null));
        }
      }

      scope.canvas.releasePointerCapture(event.pointerId);
      scope.canvas.removeEventListener('pointerup', onPointerUp);

      scope.isDown = false;
      scope.isDrag = false;
    }
  }

  update() {
    if (!this.enabled || !this.isDirty) return;

    this.isDirty = false;

    const { dispatch, camera, hover, pointer, ray, raycaster } = this;

    ray.origin.setFromMatrixPosition(camera.matrixWorld);
    ray.direction.set(pointer.x, pointer.y, 0.5).unproject(camera).sub(ray.origin).normalize();

    const hit = raycaster.intersectLogos(ray);

    const nextHover = hit.logoId ?? null;
    if (nextHover !== (hover ?? null)) {
      dispatch(setHoverGraphicId(nextHover));
    }
  }

  dispose() {}

  private readonly dispatch: Dispatch;
  private isDown = false;
  private isDrag = false;
  isDirty = false;
  private readonly pointer = new Vector2();
  private readonly pointerDown = new Vector2();
  private readonly ray = new Ray();
}
